function toFiniteNumber(value, fallback = 0) {
    const n = Number(value);
    return Number.isFinite(n) ? n : fallback;
}

function parseMonthLikeDate(value) {
    if (!value) return null;
    if (value instanceof Date && Number.isFinite(value.getTime())) {
        return new Date(Date.UTC(value.getUTCFullYear(), value.getUTCMonth(), 1));
    }

    const raw = String(value || '').trim();
    if (!raw) return null;

    const yyyyMmDd = raw.match(/^(\d{4})-(\d{2})(?:-(\d{2}))?$/);
    if (yyyyMmDd) {
        const year = Number(yyyyMmDd[1]);
        const monthIndex = Number(yyyyMmDd[2]) - 1;
        if (Number.isFinite(year) && Number.isFinite(monthIndex) && monthIndex >= 0 && monthIndex <= 11) {
            return new Date(Date.UTC(year, monthIndex, 1));
        }
    }

    const monthYear = raw.match(/^([A-Za-z]+)\s+(\d{4})$/);
    if (monthYear) {
        const monthName = String(monthYear[1] || '').trim().toLowerCase();
        const year = Number(monthYear[2]);
        const months = {
            january: 0, jan: 0,
            february: 1, feb: 1,
            march: 2, mar: 2,
            april: 3, apr: 3,
            may: 4,
            june: 5, jun: 5,
            july: 6, jul: 6,
            august: 7, aug: 7,
            september: 8, sept: 8, sep: 8,
            october: 9, oct: 9,
            november: 10, nov: 10,
            december: 11, dec: 11
        };
        const monthIndex = months[monthName];
        if (Number.isFinite(year) && Number.isFinite(monthIndex)) {
            return new Date(Date.UTC(year, monthIndex, 1));
        }
    }

    const parsed = new Date(raw);
    if (!Number.isNaN(parsed.getTime())) {
        return new Date(Date.UTC(parsed.getUTCFullYear(), parsed.getUTCMonth(), 1));
    }

    return null;
}

function getAbsoluteMonth(date) {
    if (!(date instanceof Date) || !Number.isFinite(date.getTime())) return null;
    return date.getUTCFullYear() * 12 + date.getUTCMonth();
}

export function computeProgrammeDurationMonths(programmeRow) {
    const durationMonthsRaw = programmeRow?.duration_months;
    const durationWeeksRaw = programmeRow?.duration_weeks;

    const hasMonths = durationMonthsRaw !== null && durationMonthsRaw !== undefined && String(durationMonthsRaw) !== '';
    if (hasMonths) {
        const n = Number(durationMonthsRaw);
        return Number.isFinite(n) ? n : null;
    }

    const hasWeeks = durationWeeksRaw !== null && durationWeeksRaw !== undefined && String(durationWeeksRaw) !== '';
    if (hasWeeks) {
        const w = Number(durationWeeksRaw);
        if (!Number.isFinite(w)) return null;
        return Math.round(w / 4.345);
    }

    return null;
}

export function buildYearlyInvoiceRows(project, year) {
    const initialRow = new Array(12).fill(0);
    const interimRow = new Array(12).fill(0);

    const yearNum = Number(year);
    if (!Number.isFinite(yearNum)) return { initialRow, interimRow, combined: initialRow.map(() => 0) };

    const yearBaseAbsMonth = yearNum * 12;

    const instructionDate = parseMonthLikeDate(project?.instructionDate);
    const initialFee = Math.max(0, toFiniteNumber(project?.initialReportFee));
    const instructionAbsMonth = getAbsoluteMonth(instructionDate);
    if (initialFee > 0 && instructionAbsMonth != null) {
        const offset = instructionAbsMonth - yearBaseAbsMonth;
        if (offset >= 0 && offset < 12) initialRow[offset] += initialFee;
    }

    const startOnSiteDate = parseMonthLikeDate(project?.startOnSiteDate);
    const startAbsMonth = getAbsoluteMonth(startOnSiteDate);
    const interimFee = Math.max(0, toFiniteNumber(project?.interimReportFee));
    const durationMonths = Math.max(0, Math.trunc(toFiniteNumber(project?.durationMonths)));

    if (interimFee > 0 && startAbsMonth != null) {
        if (durationMonths > 0) {
            for (let i = 0; i < durationMonths; i += 1) {
                const absoluteMonth = startAbsMonth + i;
                const offset = absoluteMonth - yearBaseAbsMonth;
                if (offset >= 0 && offset < 12) interimRow[offset] += interimFee;
            }
        } else {
            const offset = startAbsMonth - yearBaseAbsMonth;
            if (offset >= 0 && offset < 12) interimRow[offset] += interimFee;
        }
    }

    return {
        initialRow,
        interimRow,
        combined: initialRow.map((v, idx) => v + interimRow[idx])
    };
}

export function computeMonthAndYearInvoiceTotals(projects, date = new Date()) {
    const now = date instanceof Date && Number.isFinite(date.getTime()) ? date : new Date();
    const currentYear = now.getFullYear();
    const currentMonth = now.getMonth();

    let currentMonthTotal = 0;
    let yearTotal = 0;

    (Array.isArray(projects) ? projects : []).forEach((project) => {
        const rows = buildYearlyInvoiceRows(project, currentYear);
        const yearSum = rows.combined.reduce((sum, n) => sum + n, 0);
        yearTotal += yearSum;
        currentMonthTotal += Number(rows.combined[currentMonth] || 0);
    });

    return { currentMonthTotal, yearTotal };
}
